import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  MapPin, 
  Scale, 
  Phone, 
  CheckCircle2, 
  ChevronRight, 
  Shield, 
  Clock,
  Euro,
  Star,
  Users,
  Award,
  ArrowRight,
  Building2,
  Home,
  Tv,
  Wifi,
  Smartphone,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Partner logos - lokaal gehost
const LOGOS = {
  orangePartner: "/images/logos/orange-partner.png",
  telenet: "/images/logos/telenet.svg",
  proximus: "/images/logos/proximus.svg",
  base: "/images/logos/base.svg",
  orange: "/images/logos/orange.svg"
};

const HomePage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [formStarted, setFormStarted] = useState(false);
  const [formCompleted, setFormCompleted] = useState(false);
  
  const [formData, setFormData] = useState({
    postcode: '',
    customer_type: 'particulier',
    address: '',
    current_providers: [],
    services: [],
    mobile_count: 1,
    tv_decoder_count: 1,
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    privacy_accepted: false
  });

  // Load SEO settings, update document title, and load Google Analytics
  useEffect(() => {
    const loadSeoSettings = async () => {
      try {
        const response = await axios.get(`${API}/seo-settings`);
        if (response.data.meta_title) {
          document.title = response.data.meta_title;
        }
        // Update meta description
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc && response.data.meta_description) {
          metaDesc.setAttribute('content', response.data.meta_description);
        }
        // Update meta keywords
        let metaKeywords = document.querySelector('meta[name="keywords"]');
        if (response.data.meta_keywords) {
          if (!metaKeywords) {
            metaKeywords = document.createElement('meta');
            metaKeywords.setAttribute('name', 'keywords');
            document.head.appendChild(metaKeywords);
          }
          metaKeywords.setAttribute('content', response.data.meta_keywords);
        }
        // Load Google Analytics if configured
        if (response.data.google_analytics_id && !document.querySelector('script[src*="googletagmanager"]')) {
          const gaId = response.data.google_analytics_id;
          const script1 = document.createElement('script');
          script1.async = true;
          script1.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
          document.head.appendChild(script1);
          
          const script2 = document.createElement('script');
          script2.textContent = `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${gaId}');
          `;
          document.head.appendChild(script2);
        }
      } catch (error) {
        console.log('Could not load SEO settings');
      }
    };
    loadSeoSettings();
  }, []);

  // Form abandonment tracking - track when user leaves without completing
  useEffect(() => {
    const trackAbandonment = async () => {
      if (formStarted && !formCompleted && currentStep > 1) {
        try {
          await axios.post(`${API}/form-abandonment?session_id=${sessionId}&last_step=${currentStep}&postcode=${formData.postcode || ''}&user_agent=${encodeURIComponent(navigator.userAgent)}`);
        } catch (err) {
          // Silent fail - don't disrupt user experience for analytics
          console.debug('Form abandonment tracking failed:', err.message);
        }
      }
    };

    const handleBeforeUnload = () => {
      if (formStarted && !formCompleted && currentStep > 1) {
        // Use sendBeacon for reliable tracking on page unload
        navigator.sendBeacon(`${API}/form-abandonment?session_id=${sessionId}&last_step=${currentStep}&postcode=${formData.postcode || ''}`);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        trackAbandonment();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [formStarted, formCompleted, currentStep, sessionId, formData.postcode]);

  const totalSteps = 6;
  const providers = ['Telenet', 'Proximus', 'Base', 'Orange', 'Andere', 'Geen'];
  const services = ['Internet', 'TV', 'Mobiel', 'Vast telefoon'];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleProvider = (provider) => {
    setFormData(prev => ({
      ...prev,
      current_providers: prev.current_providers.includes(provider)
        ? prev.current_providers.filter(p => p !== provider)
        : [...prev.current_providers, provider]
    }));
  };

  const toggleService = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1: return formData.postcode.length >= 4;
      case 2: return formData.customer_type && formData.address.length > 0;
      case 3: return formData.current_providers.length > 0;
      case 4: return formData.services.length > 0;
      case 5: return formData.first_name && formData.last_name;
      case 6: return formData.email && formData.phone && formData.privacy_accepted;
      default: return false;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      // Scroll to form section instead of top to prevent "jumping" on mobile
      setTimeout(() => {
        const formSection = document.getElementById('form-section');
        if (formSection) {
          formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 50);
    } else {
      toast.error('Vul alle verplichte velden in');
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const submitForm = async () => {
    if (!validateStep(6)) {
      toast.error('Vul alle verplichte velden in en accepteer de privacyverklaring');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads`, formData);
      setFormCompleted(true); // Mark form as completed to prevent abandonment tracking
      toast.success('Uw aanvraag is ontvangen!');
      navigate('/bedankt', { state: { formData } });
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Er ging iets mis. Probeer opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    if (currentStep > 1) {
      // If already in form, scroll to form section
      setTimeout(() => {
        const formSection = document.getElementById('form-section');
        if (formSection) {
          formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      // Scroll to hero postcode input
      const heroForm = document.getElementById('comparison-form');
      if (heroForm) {
        heroForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Focus on postcode input
        setTimeout(() => {
          const postcodeInput = document.querySelector('[data-testid="hero-postcode-input"]');
          if (postcodeInput) {
            postcodeInput.focus();
          }
        }, 500);
      }
    }
  };

  const faqItems = [
    {
      id: 'faq-how',
      q: "Hoe werkt de prijsvergelijking?",
      a: "U vult uw postcode en gegevens in, wij vergelijken uw huidige situatie bij Telenet, Proximus of Base met het aanbod van Orange. Binnen 24 uur neemt een specialist contact met u op met een persoonlijk besparingsoverzicht."
    },
    {
      id: 'faq-free',
      q: "Is de vergelijking echt gratis?",
      a: "Ja, de vergelijking en advies is 100% gratis en vrijblijvend. U zal nooit een factuur van ons ontvangen."
    },
    {
      id: 'faq-bound',
      q: "Ben ik ergens aan gebonden?",
      a: "Nee, bij het invullen van de vergelijking bent u nergens aan gebonden. Wij doen enkel een prijsvergelijking. Uw gegevens worden daarnaast niet doorgestuurd naar onze partners, enkel moest u beslissen in te gaan op ons voorstel."
    },
    {
      id: 'faq-contract',
      q: "Wat als ik nog een lopend contract heb?",
      a: "Geen probleem! Wij bekijken samen uw volledige huidige situatie en berekenen of en wanneer overstappen voordelig is voor u."
    },
    {
      id: 'faq-discount',
      q: "Hebben jullie exclusieve kortingen?",
      a: "Ja! Wij zijn officieel partner van Orange via distributiekanaal SmartSN en hebben toegang tot exclusieve kortingen die niet altijd rechtstreeks beschikbaar zijn."
    }
  ];

  // Form Steps Content
  const renderFormContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-slate-900">Wat is uw postcode?</h3>
            <p className="text-slate-600">We checken de beschikbaarheid in uw regio.</p>
            <Input
              type="text"
              placeholder="bv. 2000"
              value={formData.postcode}
              onChange={(e) => handleInputChange('postcode', e.target.value)}
              className="h-14 text-lg"
              data-testid="postcode-input"
              maxLength={4}
            />
          </div>
        );
      
      case 2:
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-slate-900">Particulier of zakelijk?</h3>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleInputChange('customer_type', 'particulier')}
                className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${
                  formData.customer_type === 'particulier' 
                    ? 'border-blue-600 bg-blue-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
                data-testid="type-particulier"
              >
                <Home className="w-6 h-6 text-blue-600" />
                <span className="font-medium">Particulier</span>
              </button>
              <button
                type="button"
                onClick={() => handleInputChange('customer_type', 'zakelijk')}
                className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${
                  formData.customer_type === 'zakelijk' 
                    ? 'border-blue-600 bg-blue-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
                data-testid="type-zakelijk"
              >
                <Building2 className="w-6 h-6 text-blue-600" />
                <span className="font-medium">Zakelijk</span>
              </button>
            </div>
            <div className="pt-2">
              <Label htmlFor="address">Straat en huisnummer</Label>
              <Input
                id="address"
                placeholder="bv. Schoolstraat 1"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                className="mt-1"
                data-testid="address-input"
              />
            </div>
          </div>
        );
      
      case 3:
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-slate-900">Uw huidige provider(s)?</h3>
            <p className="text-slate-600">Selecteer één of meerdere providers.</p>
            <div className="grid grid-cols-2 gap-3">
              {providers.map(provider => (
                <button
                  key={provider}
                  type="button"
                  onClick={() => toggleProvider(provider)}
                  className={`p-3 rounded-lg border-2 font-medium transition-all ${
                    formData.current_providers.includes(provider)
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`provider-${provider.toLowerCase()}`}
                >
                  {provider}
                </button>
              ))}
            </div>
          </div>
        );
      
      case 4:
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-slate-900">Welke diensten zoekt u?</h3>
            <p className="text-slate-600">Selecteer alle diensten die u nodig heeft.</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { name: 'Internet', icon: Wifi },
                { name: 'TV', icon: Tv },
                { name: 'Mobiel', icon: Smartphone },
                { name: 'Vast telefoon', icon: Phone }
              ].map(({ name, icon: Icon }) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => toggleService(name)}
                  className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${
                    formData.services.includes(name)
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`service-${name.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon className={`w-6 h-6 ${formData.services.includes(name) ? 'text-blue-600' : 'text-slate-400'}`} />
                  <span className="font-medium">{name}</span>
                </button>
              ))}
            </div>
            
            {/* Extra vragen voor Mobiel */}
            {formData.services.includes('Mobiel') && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                <Label htmlFor="mobile_count" className="text-blue-900 font-medium">Hoeveel mobiele abonnementen?</Label>
                <div className="flex items-center gap-3 mt-2">
                  <button
                    type="button"
                    onClick={() => handleInputChange('mobile_count', Math.max(1, formData.mobile_count - 1))}
                    className="w-10 h-10 rounded-lg border border-blue-200 bg-white flex items-center justify-center text-blue-600 font-bold hover:bg-blue-100"
                  >
                    -
                  </button>
                  <span className="text-xl font-bold text-blue-700 w-8 text-center">{formData.mobile_count}</span>
                  <button
                    type="button"
                    onClick={() => handleInputChange('mobile_count', Math.min(10, formData.mobile_count + 1))}
                    className="w-10 h-10 rounded-lg border border-blue-200 bg-white flex items-center justify-center text-blue-600 font-bold hover:bg-blue-100"
                  >
                    +
                  </button>
                </div>
              </div>
            )}
            
            {/* Extra vragen voor TV */}
            {formData.services.includes('TV') && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                <Label htmlFor="tv_decoder_count" className="text-blue-900 font-medium">Hoeveel TV-decoders?</Label>
                <div className="flex items-center gap-3 mt-2">
                  <button
                    type="button"
                    onClick={() => handleInputChange('tv_decoder_count', Math.max(1, formData.tv_decoder_count - 1))}
                    className="w-10 h-10 rounded-lg border border-blue-200 bg-white flex items-center justify-center text-blue-600 font-bold hover:bg-blue-100"
                  >
                    -
                  </button>
                  <span className="text-xl font-bold text-blue-700 w-8 text-center">{formData.tv_decoder_count}</span>
                  <button
                    type="button"
                    onClick={() => handleInputChange('tv_decoder_count', Math.min(10, formData.tv_decoder_count + 1))}
                    className="w-10 h-10 rounded-lg border border-blue-200 bg-white flex items-center justify-center text-blue-600 font-bold hover:bg-blue-100"
                  >
                    +
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      
      case 5:
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-slate-900">Uw naam</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="first_name">Voornaam</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  className="mt-1"
                  data-testid="firstname-input"
                />
              </div>
              <div>
                <Label htmlFor="last_name">Achternaam</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  className="mt-1"
                  data-testid="lastname-input"
                />
              </div>
            </div>
          </div>
        );
      
      case 6:
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-slate-900">Contactgegevens</h3>
            <div className="space-y-3">
              <div>
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="mt-1"
                  data-testid="email-input"
                />
              </div>
              <div>
                <Label htmlFor="phone">Telefoonnummer</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="mt-1"
                  data-testid="phone-input"
                />
              </div>
            </div>
            
            {/* Privacy section with reassuring message */}
            <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-green-800">Uw gegevens zijn veilig bij ons</p>
                  <p className="text-xs text-green-600">Wij gebruiken uw gegevens enkel voor deze prijsvergelijking</p>
                </div>
              </div>
              <div className="flex items-center gap-3 pl-1">
                <Checkbox
                  id="privacy"
                  checked={formData.privacy_accepted}
                  onCheckedChange={(checked) => handleInputChange('privacy_accepted', checked)}
                  data-testid="privacy-checkbox"
                  className="border-green-400 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                />
                <Label htmlFor="privacy" className="text-sm text-slate-700 cursor-pointer">
                  Ik ga akkoord met het <Link to="/privacybeleid-en-algemene-voorwaarden" className="text-slate-700 underline hover:text-slate-900">privacybeleid en de algemene voorwaarden</Link>
                </Label>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="text-xl font-bold text-blue-600">Telecomprijsvergelijker<span className="text-orange-500">.be</span></div>
            </Link>
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-orange-50 rounded-full">
                <img src={LOGOS.orange} alt="Orange" className="h-5" />
                <span className="text-xs font-medium text-orange-700">Officieel Partner</span>
              </div>
              <Button onClick={scrollToForm} className="bg-blue-600 hover:bg-blue-700" data-testid="header-cta">
                Vergelijk nu
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-white to-slate-50 pt-12 pb-16 lg:pt-20 lg:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text & Form Start */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                <Award className="w-4 h-4" />
                Officieel Orange Partner
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
                Bespaar tot <span className="text-blue-600">€750/jaar</span> op TV, internet en mobiel
              </h1>
              
              <p className="text-lg text-slate-600 leading-relaxed">
                Vergelijk gratis Telenet, Proximus en Base met Orange. 
                Binnen 24 uur persoonlijk advies van onze specialisten.
              </p>

              {/* Quick Stats */}
              <div className="flex flex-wrap gap-6 py-4">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <div className="font-bold text-slate-900">2.500+</div>
                    <div className="text-sm text-slate-500">Klanten geholpen</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Euro className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-bold text-slate-900">€750</div>
                    <div className="text-sm text-slate-500">Gem. besparing</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <div className="font-bold text-slate-900">24 uur</div>
                    <div className="text-sm text-slate-500">Reactietijd</div>
                  </div>
                </div>
              </div>

              {/* Hero Form Start */}
              <div className="bg-white p-6 rounded-xl shadow-lg border border-slate-100" id="comparison-form">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-slate-900">Start uw gratis vergelijking</span>
                </div>
                <div className="flex gap-3">
                  <Input
                    type="text"
                    placeholder="Uw postcode"
                    value={formData.postcode}
                    onChange={(e) => handleInputChange('postcode', e.target.value)}
                    className="h-12 text-lg flex-1"
                    data-testid="hero-postcode-input"
                    maxLength={4}
                  />
                  <Button 
                    onClick={() => { 
                      if (formData.postcode.length >= 4) { 
                        setCurrentStep(2);
                        setFormStarted(true); // Mark form as started for abandonment tracking
                        // Wait for React to render the form section before scrolling
                        setTimeout(() => {
                          const formSection = document.getElementById('form-section');
                          if (formSection) {
                            formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }, 150);
                      } else { 
                        toast.error('Vul een geldige postcode in'); 
                      } 
                    }}
                    className="h-12 px-6 bg-blue-600 hover:bg-blue-700"
                    data-testid="hero-compare-button"
                  >
                    Vergelijk <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-3 flex items-center gap-1">
                  <Shield className="w-3 h-3" /> 100% gratis & vrijblijvend
                </p>
              </div>
            </div>

            {/* Right Column - Visual */}
            <div className="hidden lg:block relative">
              <div className="aspect-square bg-gradient-to-br from-blue-100 to-orange-50 rounded-3xl overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/4260744/pexels-photo-4260744.jpeg" 
                  alt="Gelukkige familie met telecom" 
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-xl border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <div className="font-bold text-green-600">€42/maand</div>
                    <div className="text-sm text-slate-500">bespaard</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar - Provider Logos */}
      <section className="py-10 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-base font-medium text-slate-600 mb-6">Wij vergelijken alle grote providers</p>
          <div className="flex flex-wrap justify-center items-center gap-10 md:gap-16">
            <img src={LOGOS.telenet} alt="Telenet" className="h-9 md:h-10" />
            <img src={LOGOS.proximus} alt="Proximus" className="h-9 md:h-10" />
            <img src={LOGOS.base} alt="Base" className="h-9 md:h-10" />
            <img src={LOGOS.orange} alt="Orange" className="h-9 md:h-10" />
          </div>
        </div>
      </section>

      {/* Multi-Step Form Section */}
      {currentStep > 1 && (
        <section className="py-16 bg-slate-50" id="form-section">
          <div className="max-w-xl mx-auto px-4">
            <Card className="shadow-lg border-slate-100">
              <CardContent className="p-6 md:p-8">
                {/* Progress Bar */}
                <div className="mb-8">
                  <div className="flex justify-between text-sm text-slate-500 mb-2">
                    <span>Stap {currentStep} van {totalSteps}</span>
                    <span>{Math.round((currentStep / totalSteps) * 100)}%</span>
                  </div>
                  <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-blue-600 transition-all duration-300"
                      style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Form Content - min-height prevents layout shift on mobile */}
                <div className="min-h-[320px] sm:min-h-[280px]">
                  {renderFormContent()}
                </div>

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-8 pt-6 border-t border-slate-100">
                  <Button 
                    variant="outline" 
                    onClick={prevStep}
                    data-testid="prev-button"
                  >
                    Vorige
                  </Button>
                  
                  {currentStep < totalSteps ? (
                    <Button 
                      onClick={nextStep}
                      disabled={!validateStep(currentStep)}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="next-button"
                    >
                      Volgende <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  ) : (
                    <Button 
                      onClick={submitForm} 
                      disabled={isSubmitting || !validateStep(6)}
                      className="bg-orange-600 hover:bg-orange-700"
                      data-testid="submit-button"
                    >
                      {isSubmitting ? 'Bezig...' : 'Verstuur aanvraag'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Hoe werkt het?</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">In 3 simpele stappen naar een lagere factuur voor TV, internet en mobiel</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { id: 'step-postcode', icon: MapPin, title: "1. Vul uw postcode in", desc: "Check beschikbaarheid in uw regio en start de vergelijking." },
              { id: 'step-compare', icon: Scale, title: "2. Vergelijk aanbod", desc: "Wij vergelijken uw noden en bieden u het beste aanbod bij Orange aan." },
              { id: 'step-switch', icon: Home, title: "3. Gratis overstapservice", desc: "Wij komen tot bij u langs voor de nodige uitleg en papierwerk over te nemen, volledig gratis." }
            ].map(({ id, icon: Icon, title, desc }) => (
              <div key={id} className="text-center p-8 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
                  <Icon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{title}</h3>
                <p className="text-slate-600">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Waarom Telecomprijsvergelijker?</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { id: 'usp-exp', icon: Award, title: "10+ jaar ervaring", desc: "Duizenden klanten succesvol geholpen" },
              { id: 'usp-save', icon: Euro, title: "Tot €750 besparen", desc: "Gemiddeld €62/maand minder betalen" },
              { id: 'usp-free', icon: Shield, title: "100% gratis", desc: "Geen verborgen kosten, nooit" },
              { id: 'usp-fast', icon: Clock, title: "Binnen 24 uur", desc: "Snelle, persoonlijke service" }
            ].map(({ id, icon: Icon, title, desc }) => (
              <div key={id} className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                <Icon className="w-10 h-10 text-blue-600 mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{title}</h3>
                <p className="text-slate-600 text-sm">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Wat onze klanten zeggen</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { id: 'review-marian', name: "Marian", location: "Meulebeke", text: "Fantastische service! Bespaar nu €35/maand op mijn Telenet pakket. De overstap was vlekkeloos.", rating: 5 },
              { id: 'review-eva', name: "Eva", location: "Torhout", text: "Heel blij dat ik vergeleken heb. Veel beter aanbod gekregen dan bij mijn oude provider.", rating: 5 },
              { id: 'review-jolien', name: "Jolien", location: "Ledegem", text: "Professioneel advies en snelle service. Aanrader voor iedereen die wil besparen op telecom.", rating: 5 }
            ].map((testimonial) => (
              <div key={testimonial.id} className="bg-slate-50 p-6 rounded-xl border border-slate-100">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={`${testimonial.id}-star-${i}`} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-700 mb-4">"{testimonial.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="font-semibold text-blue-600">{testimonial.name[0]}</span>
                  </div>
                  <div>
                    <div className="font-medium text-slate-900">{testimonial.name}</div>
                    <div className="text-sm text-slate-500">{testimonial.location}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Veelgestelde vragen</h2>
          </div>
          
          <div className="space-y-4">
            {faqItems.map((item) => (
              <div key={item.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <button
                  className="w-full p-5 text-left flex justify-between items-center"
                  onClick={() => setExpandedFaq(expandedFaq === item.id ? null : item.id)}
                >
                  <span className="font-semibold text-slate-900">{item.q}</span>
                  {expandedFaq === item.id ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </button>
                {expandedFaq === item.id && (
                  <div className="px-5 pb-5 text-slate-600">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Klaar om te besparen?
          </h2>
          <p className="text-blue-100 text-lg mb-8">
            Start nu uw gratis vergelijking en ontdek hoeveel u kunt besparen.
          </p>
          <Button 
            onClick={scrollToForm}
            size="lg"
            className="bg-white text-blue-600 hover:bg-blue-50 font-semibold px-8 py-6 text-lg"
            data-testid="final-cta-button"
          >
            Start vergelijking <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="text-xl font-bold text-white mb-4">
                Telecomprijsvergelijker<span className="text-orange-500">.be</span>
              </div>
              <p className="text-sm mb-4">
                Wij helpen u besparen op TV, internet en mobiel door Telenet, Proximus en Base te vergelijken met Orange's aanbod.
              </p>
              <div className="flex items-center gap-2 bg-slate-800 rounded-lg px-3 py-2 inline-flex">
                <img src={LOGOS.orange} alt="Orange" className="h-6" />
                <span className="text-sm text-white font-medium">Officieel Partner</span>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Links</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacybeleid-en-algemene-voorwaarden" className="hover:text-white transition-colors">Privacybeleid en algemene voorwaarden</Link></li>
                <li><Link to="/cookies" className="hover:text-white transition-colors">Cookiebeleid</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/contact" className="hover:text-white transition-colors flex items-center gap-2">
                    <ArrowRight className="w-4 h-4" />
                    Contactformulier
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm">
            <p className="mb-2">Officieel Orange partner via distributiekanaal SmartSN</p>
            <p>© {new Date().getFullYear()} Telecomprijsvergelijker.be - Alle rechten voorbehouden</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
